import { Chip, Grid, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowBack, ArrowForward, Link as MuiLinkIcon } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { HeaderSaveButton } from './saveHeader/HeaderSaveButton';
import { SaveHeaderProvider, useSaveHeader } from './saveHeader/SaveHeaderContext';
import { SettingsPageTabs } from './SettingsPageTabs';
import RotatingLoader from '../common/ui/RotatingLoader';
import {
  type BoolPriceData,
  Currency,
  type EnumPriceData,
  type GTPriceData,
  Language,
  type MultipleTicketsPriceData,
  type NumberPriceData,
  OrderingCutoffSettingsData,
  OrderingCutoffStrategy,
  OrderPageDataTypes,
  OrderPageTypes,
  PartialLabelData,
  ProjectSelectionConfigData,
  SaveCardConfigData,
  VouchersConfigData,
  WorkingDaysData,
} from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { theme, useLanguage } from '../common/GeneralUtils';
import { InformationSection } from './sections/informationSection/InformationSection';
import { SettingsSection } from './sections/settingsSection/SettingsSection';
import { PricingSection } from './sections/pricingSection/PricingSection';
import { WorkingDaysSection } from './sections/workingDays/WorkingDaysSection';
import { EventScheduleSection } from './sections/eventSchedule/EventScheduleSection';
import { RestrictionsSection } from './sections/restrictionsSection/RestrictionsSection';
import { CommunicationSection } from './sections/communicationSection/CommunicationSection';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { DeletionSection } from './sections/deletionSection/DeletionSection';
import { TabsPage } from '../common/components/TabsPage';
import { getOrderPage } from '../utils/AccountUtils';
import { WidgetSection } from './sections/widgetSection/WidgetSection';
import { IntegrationSection } from './sections/integrationSection/integrationSection';
import { SMSSection } from './sections/smsSection/SMSSection';
import CopyToClipboardButton from '../calendar/dialogs/CopyToClipboardButton';
import TTLLocalStorage from '../utils/TTLLocalStorage';
import { DEFAULT_ORDER_PAGE_COLOR } from '../common/theme/mbTokens';

export enum PriceTypes {
  ENUM = 'enum',
  NUMBER = 'number',
  BOOL = 'bool',
  GT = 'gt',
  MULTIPLE_TICKETS_PRICE = 'multiple_tickets_price',
}

export type OrderPagePrice = {
  type: PriceTypes;
  data_type: OrderPageDataTypes;
  name?: string;
  price?: number;
  base_value: number;
  base_price: number;
  price_per_additional_value?: number;
  tiered_pricing?: {
    quantity: number;
    price_per_unit: number;
  }[];
  custom_per_unit_pricing?: number[];
};
const ROW_BASED_TABS: SettingsPageTabs[] = [
  SettingsPageTabs.INFORMATION,
  SettingsPageTabs.PRICING,
  SettingsPageTabs.SETTINGS,
  SettingsPageTabs.RESTRICTIONS,
  SettingsPageTabs.WIDGET,
  SettingsPageTabs.INTEGRATION,
  SettingsPageTabs.DELETION,
];

export type OrderPageUpdate = {
  order_page_type: string;
  label: PartialLabelData;
  description: PartialLabelData;
  location: PartialLabelData;
  // waiting_list: WaitingListData; not implemented yet
  color: string;
  ordering_cutoff_settings?: OrderingCutoffSettingsData;
  max_date_in_days: number;
  after_buffer_minutes: number;
  duration: number;
  working_days?: WorkingDaysData;
  price: (EnumPriceData | NumberPriceData | BoolPriceData | GTPriceData | MultipleTicketsPriceData)[];
  availability_settings: {
    parallel_order_page_ids?: string[];
    blocks_private_when_empty?: boolean;
  };
  wait_list_enabled?: boolean;
  weekend_percentage_enabled?: boolean;
  weekend_percentage?: number;
  // Every section PUTs the whole order page, so this has to live on the shared type --
  // otherwise saving from another tab would drop the config.
  save_card_config?: SaveCardConfigData;
  project_selection_config?: ProjectSelectionConfigData;
  price_unit_label?: PartialLabelData;
  show_price_as_deposit?: boolean;
  contact_details_optional_in_backoffice?: boolean;
  related_order_page_ids?: string[];
  vouchers_config?: VouchersConfigData;
};

export const OrderPageSettingsPage = () => {
  const language = useLanguage();
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const { orderPageId } = useParams();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const posthog = usePostHog();
  useEffect(() => {
    posthog.capture('Editing order page loaded');
  }, []);
  const { t } = useTranslation();
  const { authState } = useAuth();

  const orderPage = getOrderPage(account, orderPageId || '');
  const isEventOrderPage = orderPage?.order_page_type === OrderPageTypes.EVENT;
  const isCourseOrderPage = orderPage?.order_page_type === OrderPageTypes.COURSE;
  const isEventLikeOrderPage = isEventOrderPage || isCourseOrderPage;
  const eventScheduleEnabled = useFeatureFlagEnabled('feature-event-schedule');
  const bookingPageLink = `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/experience/${account?.id}/`;

  if (!authState.isInitialized || !account || !orderPage) {
    return (
      <Grid
        container
        sx={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <RotatingLoader />
      </Grid>
    );
  }

  const initialValues: OrderPageUpdate = {
    ...orderPage,
    description: orderPage.description || {},
    location: orderPage.location || {},
    ordering_cutoff_settings: orderPage.ordering_cutoff_settings || {
      strategy: OrderingCutoffStrategy.DAILY_CUTOFF,
      minimum_days_ahead: 0,
      cutoff_time_hour: 18,
      cutoff_time_minute: 0,
    },
    color: orderPage?.color || DEFAULT_ORDER_PAGE_COLOR,
    duration: (orderPage?.duration || 0) / 60,
    working_days: orderPage?.working_days ? orderPage.working_days : account.experiences[0].working_days,
    price: orderPage?.price,
    wait_list_enabled: orderPage.waiting_list?.enabled,
  };
  const tabsToShow = Object.values(SettingsPageTabs).filter((tab) => {
    if (isEventLikeOrderPage && tab === SettingsPageTabs.WORKING_DAYS) return false;
    if (tab === SettingsPageTabs.EVENT_SCHEDULE) {
      if (!isEventOrderPage) return false;
      if (!eventScheduleEnabled) return false;
    }
    return true;
  });
  const tabContent: Partial<Record<SettingsPageTabs, React.ReactElement>> = {
    [SettingsPageTabs.INFORMATION]: (
      <InformationSection
        orderPage={orderPage}
        languages={account.customer_facing_widgets_languages}
        orderPageInitialValues={initialValues}
        updateLocalAccount={updateLocalAccount}
        account={account}
      />
    ),
    [SettingsPageTabs.SETTINGS]: (
      <SettingsSection
        orderPage={orderPage}
        orderPageInitialValues={initialValues}
        updateLocalAccount={updateLocalAccount}
      />
    ),
    [SettingsPageTabs.PRICING]: (
      <PricingSection
        orderPage={orderPage}
        orderPageInitialValues={initialValues}
        updateLocalAccount={updateLocalAccount}
      />
    ),
    [SettingsPageTabs.WORKING_DAYS]: (
      <WorkingDaysSection
        accountWorkingDays={account.experiences[0].working_days}
        orderPage={orderPage}
        orderPageInitialValues={initialValues}
        updateLocalAccount={updateLocalAccount}
      />
    ),
    [SettingsPageTabs.EVENT_SCHEDULE]: (
      <EventScheduleSection
        orderPage={orderPage}
        orderPageInitialValues={initialValues}
        updateLocalAccount={updateLocalAccount}
        account={account}
      />
    ),
    [SettingsPageTabs.RESTRICTIONS]: (
      <RestrictionsSection
        orderPage={orderPage}
        orderPageInitialValues={initialValues}
        updateLocalAccount={updateLocalAccount}
        account={account}
      />
    ),
    [SettingsPageTabs.COMMUNICATION]: <CommunicationSection orderPage={orderPage} />,
    [SettingsPageTabs.WIDGET]: (
      <WidgetSection
        orderPage={orderPage}
        orderPageInitialValues={initialValues}
        updateLocalAccount={updateLocalAccount}
      />
    ),
    [SettingsPageTabs.INTEGRATION]: <IntegrationSection orderPage={orderPage} account={account} />,
  };

  if (account.currency === Currency.USD) {
    tabContent[SettingsPageTabs.SMS] = <SMSSection orderPage={orderPage} />;
  }

  tabContent[SettingsPageTabs.DELETION] = (
    <DeletionSection orderPage={orderPage} updateLocalAccount={updateLocalAccount} account={account} />
  );
  const filteredTabContent = Object.keys(tabContent)
    .filter((key) => tabsToShow.includes(key as SettingsPageTabs))
    .reduce((obj: any, key) => {
      // The content card pads 8px 28px for the sections built from `SettingsRow`s (which carry their own 24px);
      // the free-form sections get the vertical breathing room here.
      const content = tabContent[key as SettingsPageTabs];
      obj[key] = ROW_BASED_TABS.includes(key as SettingsPageTabs) ? content : <Grid sx={{ py: 2 }}>{content}</Grid>;
      return obj;
    }, {} as typeof tabContent);

  return (
    <SaveHeaderProvider tabs={Object.keys(filteredTabContent)}>
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <OrderPageSettingsPageLayout
        isSmallScreen={isSmallScreen}
        subtitle={orderPage.label[language] || ''}
        typeLabel={
          isEventOrderPage
            ? t('editOrderPage.orderPageTypeEvent')
            : isCourseOrderPage
            ? t('editOrderPage.orderPageTypeCourse')
            : t('editOrderPage.orderPageTypePrivate')
        }
        breadcrumbLabel={t('main_sidebar.order_pages', { activityLabel: account.activity_label_plural?.[language] })}
        bookingHref={bookingPageLink + orderPage.id}
        bookingLabel={t('order_pages.order_page')}
        orderPageId={orderPage.id}
        tabContent={filteredTabContent}
      />
    </SaveHeaderProvider>
  );
};

type LayoutProps = {
  isSmallScreen: boolean;
  subtitle: string;
  breadcrumbLabel: string;
  typeLabel: string;
  bookingHref: string;
  bookingLabel: string;
  orderPageId: string;
  tabContent: Record<string, React.ReactNode>;
};

// `PaperPage bare` owns the page inset; this layout owns the 16px gap between header and content.
const OrderPageSettingsPageLayout = ({
  isSmallScreen,
  subtitle,
  breadcrumbLabel,
  typeLabel,
  bookingHref,
  bookingLabel,
  orderPageId,
  tabContent,
}: LayoutProps) => {
  const { activeTab, setActiveTab, requestTabChange } = useSaveHeader();
  const BackIcon = useLanguage() === Language.HE ? ArrowForward : ArrowBack;
  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0, gap: 2, overflow: 'hidden' }}>
      <Grid
        container
        gap={2}
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ flexShrink: 0 }}>
        <Grid container flexDirection="column" gap={1} sx={{ minWidth: 0, flex: '1 1 auto', width: 'auto' }}>
          <Link className="no-style-link" to="/dashboard/order-pages" style={{ alignSelf: 'flex-start' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': { color: 'text.primary' },
              }}>
              <BackIcon sx={{ fontSize: 16 }} />
              {breadcrumbLabel}
            </Typography>
          </Link>
          <Typography variant="h1" sx={isSmallScreen ? { fontSize: 24 } : undefined}>
            {subtitle}
          </Typography>
          <Grid container alignItems="center" gap={1.25}>
            <Chip
              size="small"
              label={typeLabel}
              sx={{ bgcolor: 'mb.side', color: 'text.primary', borderRadius: '4px' }}
            />
            <Link
              className="no-style-link"
              to={bookingHref}
              target="_blank"
              onClick={() => TTLLocalStorage.removeByPrefix('order-page-state')}>
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}>
                <MuiLinkIcon sx={{ fontSize: 16 }} />
                {bookingLabel}
              </Typography>
            </Link>
            <Grid sx={{ color: 'primary.main', display: 'inline-flex', alignItems: 'center' }}>
              <CopyToClipboardButton
                orderPageId={orderPageId}
                text={bookingHref}
                IconComponent={MuiLinkIcon}
                color="inherit"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <HeaderSaveButton />
        </Grid>
      </Grid>
      <Grid sx={{ flex: 1, minHeight: 0 }}>
        <TabsPage
          tabContent={tabContent}
          activeTab={activeTab}
          onActiveTabChange={setActiveTab}
          guardTabChange={requestTabChange}
        />
      </Grid>
    </Grid>
  );
};
